import React, { useState, useEffect } from 'react';
import Modal from "@/components/ui/Modal";
import { Input } from '@/components/ui/custom/ui/input';
import { Button } from '@/components/ui/custom/ui/button';
import { Table, TableHeader, TableHead, TableRow, TableBody, TableCell } from '@/components/ui/custom/ui/table';
import Pagination from "@/components/ui/Pagination";
import { supabase } from '@/supabase/client';

const SearchNodePurchasedModal = ({ isOpen, onClose }) => {
  const [searchValue, setSearchValue] = useState('');
  const [searchOption, setSearchOption] = useState('');
  const [searchResult, setSearchResult] = useState([]);
  const [searchWarning, setSearchWarning] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Number of items to display per page
  const [emptySearchWarning, setEmptySearchWarning] = useState(false);
  const [invalidInputWarning, setInvalidInputWarning] = useState('');

  const walletRegex = /^0x[a-fA-F0-9]{40}$/;
  const uidRegex = /^[a-zA-Z0-9]{28}$/;

  useEffect(() => {
    setCurrentPage(1); // Reset current page when search changes
  }, [searchResult]); // Reset current page when search results change

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

   // Pagination calculation to get items for the current page
   const indexOfLastItem = currentPage * itemsPerPage;
   const indexOfFirstItem = indexOfLastItem - itemsPerPage;
   const currentItems = searchResult.slice(indexOfFirstItem, indexOfLastItem);

  const formatDateTime = (date) => {
    const formattedDate = date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
    const formattedTime = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
    
    return `${formattedDate}, ${formattedTime}`;
  };

  const handleSearch = async () => {
    // Reset all warning and result states at the beginning of the search
    setSearchWarning(false);
    setEmptySearchWarning(false);
    setNoResults(false);
    setInvalidInputWarning('');
    setSearchResult([]);

    if (searchOption === "") {
      setSearchWarning(true);
      return;
    }

    if (!searchValue.trim()) {
      setEmptySearchWarning(true);
      return;
    }

    // Check for valid input based on search option
    if (searchOption === "purchase_wallet" && !walletRegex.test(searchValue)) {
      setInvalidInputWarning("Please enter a valid Ethereum wallet address.");
      return;
    }

    if (searchOption === "uid" && !uidRegex.test(searchValue)) {
      setInvalidInputWarning("Please enter a valid UID (28 alphanumeric characters).");
      return;
    }

    try {
      if (searchOption === "uid") {
        const { data: userRow, error: userErr } = await supabase
          .from('users')
          .select('wallet_lc, wallet')
          .eq('uid', searchValue)
          .maybeSingle();

        if (userErr) throw userErr;
        if (!userRow || !userRow.wallet_lc) {
          setNoResults(true);
          return;
        }

        const { data, error } = await supabase
          .from('node_holders')
          .select('*')
          .eq('wallet_address_lc', userRow.wallet_lc);

        if (error) throw error;
        if (!data || data.length === 0) {
          setNoResults(true);
        } else {
          const mapped = [];
          data.forEach((row) => {
            const fullIds = Array.isArray(row.full_node_token_ids) ? row.full_node_token_ids : [];
            const fracIds = Array.isArray(row.fractional_node_token_ids) ? row.fractional_node_token_ids : [];
            fullIds.forEach((tokenId) => {
              mapped.push({ license_type: 'Full', node_number: Number(tokenId) });
            });
            fracIds.forEach((tokenId) => {
              mapped.push({ license_type: 'Fractional', node_number: Number(tokenId) });
            });
          });
          if (mapped.length === 0) {
            setNoResults(true);
          } else {
            setSearchResult(mapped);
          }
        }
      } else if (searchOption === "purchase_wallet") {
        const searchValueLowerCase = searchValue.toLowerCase();
        const { data, error } = await supabase
          .from('node_holders')
          .select('*')
          .eq('wallet_address_lc', searchValueLowerCase);

        if (error) throw error;
        if (!data || data.length === 0) {
          setNoResults(true);
        } else {
          const mapped = [];
          data.forEach((row) => {
            const fullIds = Array.isArray(row.full_node_token_ids) ? row.full_node_token_ids : [];
            const fracIds = Array.isArray(row.fractional_node_token_ids) ? row.fractional_node_token_ids : [];
            fullIds.forEach((tokenId) => {
              mapped.push({ license_type: 'Full', node_number: Number(tokenId) });
            });
            fracIds.forEach((tokenId) => {
              mapped.push({ license_type: 'Fractional', node_number: Number(tokenId) });
            });
          });
          if (mapped.length === 0) {
            setNoResults(true);
          } else {
            setSearchResult(mapped);
          }
        }
      } else {
        return;
      }
    } catch (error) {
      console.error("Error searching node_holders:", error);
      setNoResults(true);
    }
  };

  // Function to convert string to camel case
  const toCamelCase = (str) => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };  

  // Function to determine rarity based on node number
  const getRarity = (nodeNumber) => {
    if (nodeNumber >= 1 && nodeNumber <= 3000) {
      return "Mythical";
    } else if (nodeNumber >= 3001 && nodeNumber <= 5000) {
      return "Legendary";
    } else if (nodeNumber >= 5001 && nodeNumber <= 8000) {
      return "Epic";
    } else if (nodeNumber >= 8001 && nodeNumber <= 30000) {
      return "Standard";
    } else {
      return "Unknown";
    }
  };

  return (
    <Modal
      activeModal={isOpen}
      onClose={onClose}
      title={"Search Node"}
      className="max-w-2xl w-[95vw] sm:w-auto"
      themeClass="bg-metaxseed-blue !px-4 sm:!px-6 !py-3"
    >
      <div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <select 
            value={searchOption}
            onChange={(e) => setSearchOption(e.target.value)}
            className="w-full sm:w-40 h-9 bg-white text-black-500 text-sm  border border-[#999] rounded-md px-3"
          >
            <option value="" disabled>Search by</option>
            <option value="purchase_wallet">Wallet</option>
            <option value="uid">UID</option>
          </select>
          <Input
            type="text"
            value={searchValue}
            placeholder={searchOption === 'uid' ? 'Enter UID' : 'Enter wallet (0x...)'}
            onChange={(e) => setSearchValue(e.target.value)}
            className="bg-white text-black-500 text-sm border border-[#999]"
          />
          <Button onClick={handleSearch} className="h-9 px-4 bg-blue-500 hover:bg-blue-600 text-white w-full sm:w-auto">Search</Button>
        </div>
        {searchWarning && (
          <p className="mt-4 text-red-500">Please select an option.</p>
        )}
        {emptySearchWarning && (
          <p className="mt-4 text-red-500">Please enter a search value.</p>
        )}
        {invalidInputWarning && (
          <p className="mt-4 text-red-500">{invalidInputWarning}</p>
        )}
        {noResults && searchValue.trim() !== "" && !emptySearchWarning && !invalidInputWarning && (
          <p className="mt-4 text-white">No results found.</p>
        )}
        {currentItems.length > 0 ? (
          <div className="mt-6">
            {/* Desktop Table */}
            <div className="hidden sm:block">
              <Table className="text-white">
                <TableHeader>
                  <TableRow className='text-white text-sm xl:text-base gothamMedium'>
                    <TableHead>License Type</TableHead>
                    <TableHead>Node Number</TableHead>
                    <TableHead>Rarity</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentItems.map((result, index) => (
                    <TableRow key={index} className='text-sm xl:text-base bg-metaxseed-content-gray gothamBook'>
                      <TableCell>{result.license_type}</TableCell>
                      <TableCell>{result.node_number}</TableCell>
                      <TableCell>{getRarity(result.node_number)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            
            {/* Mobile Cards */}
            <div className="sm:hidden space-y-3">
              {currentItems.map((result, index) => (
                <div key={index} className="bg-metaxseed-content-gray rounded-lg p-4 text-white">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-metaxseed-text-gray">License Type</span>
                    <span className="text-sm gothamBook">{result.license_type}</span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-metaxseed-text-gray">Node Number</span>
                    <span className="text-sm gothamBook">{result.node_number}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-metaxseed-text-gray">Rarity</span>
                    <span className="text-sm gothamBook">{getRarity(result.node_number)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
        {searchResult.length > itemsPerPage && (
          <div className="mt-6 flex justify-center overflow-x-auto">
            <Pagination
              totalPages={Math.ceil(searchResult.length / itemsPerPage)}
              currentPage={currentPage}
              handlePageChange={handlePageChange}
              activeClassName="!bg-blue-500 !text-white"
            />
          </div>
        )}
        {/* <div className="flex justify-end mt-6">
          <Button className="bg-gray-500 hover:bg-gray-600 text-white" onClick={onClose}>Close</Button>
        </div> */}
      </div>
    </Modal>
  );
};

export default SearchNodePurchasedModal;
